import { useMedplum, useMedplumProfile } from '@medplum/react';
import { Patient } from '@medplum/fhirtypes';
import { useCallback, useMemo } from 'react';
import { useRevenueCat, isRevenueCatEnabled } from '../contexts/RevenueCatContext';

/**
 * Interface for conversation tracking results returned from the tracking bot
 */
interface ConversationTrackingResult {
  conversationsUsed: number;
  conversationsAllowed: number | null;
  remainingConversations: number | null;
  hasReachedLimit: boolean;
  isLimitEnforced: boolean;
}

/**
 * A React hook that provides subscription status and feature access information
 * for the FeelHeard application based on the user's subscription tier.
 *
 * Uses RevenueCat as the single source of truth when available,
 * with FHIR Patient extensions as fallback.
 *
 * Features included:
 * - Current subscription tier and status
 * - Voice conversation access
 * - Conversation count limits
 * - History retention
 * - Subscription management functions
 */
export function useSubscriptionFeatures() {
  const medplum = useMedplum();
  const profile = useMedplumProfile() as Patient;

  // Use RevenueCat for paid subscriptions
  const revenueCatEnabled = isRevenueCatEnabled();
  const revenueCat = revenueCatEnabled ? useRevenueCat() : null;

  // Get FHIR-based trial info (set at registration, no payment needed)
  const fhirTrialEndDate = profile?.extension?.find(
    e => e.url === 'https://progressnotes.app/fhir/StructureDefinition/trial-end-date'
  )?.valueDateTime;

  const fhirSubscriptionStatus = profile?.extension?.find(
    e => e.url === 'https://progressnotes.app/fhir/StructureDefinition/subscription-status'
  )?.valueString;

  // Check if FHIR trial is still active (not expired)
  const fhirTrialActive = useMemo(() => {
    if (fhirSubscriptionStatus !== 'trial' || !fhirTrialEndDate) return false;
    const now = new Date();
    const endDate = new Date(fhirTrialEndDate);
    return endDate.getTime() > now.getTime();
  }, [fhirSubscriptionStatus, fhirTrialEndDate]);

  // Check if trial has expired (was on trial but now past end date)
  // Only true if user has NOT subscribed via RevenueCat
  const trialExpired = useMemo(() => {
    // If user has paid subscription via RevenueCat, trial expiration doesn't matter
    if (revenueCat?.isInitialized && revenueCat.isPremium) return false;
    if (fhirSubscriptionStatus !== 'trial' || !fhirTrialEndDate) return false;
    const now = new Date();
    const endDate = new Date(fhirTrialEndDate);
    return endDate.getTime() <= now.getTime();
  }, [revenueCat?.isInitialized, revenueCat?.isPremium, fhirSubscriptionStatus, fhirTrialEndDate]);

  // Calculate days remaining in FHIR trial
  const fhirTrialDaysRemaining = useMemo(() => {
    if (!fhirTrialEndDate) return 0;
    const now = new Date();
    const endDate = new Date(fhirTrialEndDate);
    const diffTime = endDate.getTime() - now.getTime();
    return Math.max(0, Math.ceil(diffTime / (1000 * 60 * 60 * 24)));
  }, [fhirTrialEndDate]);

  // Subscription logic:
  // 1. If RevenueCat has active premium subscription → premium (paid)
  // 2. If FHIR trial is active (not expired) → trial (free trial from registration)
  // 3. Otherwise → free
  const isPremium = revenueCat?.isInitialized ? revenueCat.isPremium : false;
  const isTrialActive = !isPremium && fhirTrialActive;
  const isFree = !isPremium && !isTrialActive;
  const isBasic = false; // No basic tier

  // Determine subscription status string
  const subscriptionStatus = useMemo(() => {
    if (isPremium) return 'active';
    if (isTrialActive) return 'trial';
    return 'free';
  }, [isPremium, isTrialActive]);

  // Determine tier
  const subscriptionTier = useMemo((): 'free' | 'basic' | 'premium' => {
    if (isPremium) return 'premium';
    if (isTrialActive) return 'premium'; // Trial gives premium features
    return 'free';
  }, [isPremium, isTrialActive]);

  // Trial end date (from FHIR since that's where registration trial is stored)
  const trialEndDate = fhirTrialEndDate;

  // Days remaining in trial
  const trialDaysRemaining = isTrialActive ? fhirTrialDaysRemaining : 0;

  // Get billing cycle (monthly/annual) - only from FHIR for now
  const billingCycle = profile?.extension?.find(
    e => e.url === 'https://progressnotes.app/fhir/StructureDefinition/subscription-billing-cycle'
  )?.valueString as 'monthly' | 'annual' | undefined;

  // Legacy fields kept for compatibility
  const conversationCount = profile?.extension?.find(
    e => e.url === 'https://progressnotes.app/fhir/StructureDefinition/subscription-conversations-used'
  )?.valueInteger || 0;

  const conversationLimit = profile?.extension?.find(
    e => e.url === 'https://progressnotes.app/fhir/StructureDefinition/subscription-conversations-allowed'
  )?.valueInteger;

  const isActive = subscriptionStatus === 'active';

  console.log('[useSubscriptionFeatures] Values:', {
    revenueCatPremium: revenueCat?.isPremium,
    fhirTrialActive,
    fhirTrialDaysRemaining,
    trialExpired,
    subscriptionStatus,
    subscriptionTier,
    isPremium,
    isTrialActive,
    isFree
  });

  // Determine feature flags - no more conversation limits
  const canUseVoice = isTrialActive || isPremium;
  const hasUnlimitedHistory = isTrialActive || isPremium;
  const canStartNewConversation = isTrialActive || isPremium || isBasic;

  console.log('[useSubscriptionFeatures] Feature Flags:', {
    canUseVoice,
    hasUnlimitedHistory,
    canStartNewConversation
  });
  
  // Conversation tracking functions removed as per conversation limits removal plan
  
  /**
   * Helper to get price ID based on tier and billing cycle
   */
  const getPriceId = useCallback((tier: 'basic' | 'premium', annual: boolean): string => {
    // Determine if we're in test or prod mode
    const stripeMode = import.meta.env.VITE_STRIPE_MODE || 'TEST';
    console.log(`Using Stripe mode: ${stripeMode}`);
    
    // Use the correct price IDs based on mode
    const priceMapping = stripeMode === 'TEST' ? 
      {
        // TEST mode price IDs - Special pricing
        'basic-monthly': 'price_1RJqxuIfLgrjtRiqsg9bnkJf',  // $9.99/month (Special)
        'basic-annual': 'price_1RJr1xIfLgrjtRiqrW30KrjC',   // $95.88/year (Special)
        'premium-monthly': 'price_1RJqzPIfLgrjtRiq32aqFawQ', // $19.99/month (Special)
        'premium-annual': 'price_1RJr5IIfLgrjtRiq06WKvnNC'   // $191.88/year (Special)
        
        // Original Regular Pricing (commented out for reference)
        // 'basic-monthly': 'price_1RI3nMIfLgrjtRiq9tXej1QM',  // $14.99/month
        // 'basic-annual': 'price_1RI3nMIfLgrjtRiqPdc1FNWa',   // $143.88/year
        // 'premium-monthly': 'price_1RI3nMIfLgrjtRiqsJ2NNnSf', // $29.99/month
        // 'premium-annual': 'price_1RI3nMIfLgrjtRiqVPTBy2T2'   // $287.88/year
      } : 
      {
        // PROD mode price IDs - Special pricing
        'basic-monthly': 'price_1RJr8pIfLgrjtRiqN6l6GzdX',  // $9.99/month (Special)
        'basic-annual': 'price_1RJr9WIfLgrjtRiqR4vVZpCG',   // $95.88/year (Special)
        'premium-monthly': 'price_1RJrAFIfLgrjtRiqgQoYOpjm', // $19.99/month (Special)
        'premium-annual': 'price_1RJrBqIfLgrjtRiqtmJbnB6S'   // $191.88/year (Special)
        
        // Original Regular Pricing (commented out for reference)
        // 'basic-monthly': 'price_1RI4BPIfLgrjtRiqy0RIuD0S',  // $14.99/month
        // 'basic-annual': 'price_1RI4BPIfLgrjtRiqzs5PZvEc',   // $143.88/year
        // 'premium-monthly': 'price_1RI4BPIfLgrjtRiqGM6UJb1S', // $29.99/month
        // 'premium-annual': 'price_1RI4BPIfLgrjtRiq1faFDCth'   // $287.88/year
      };
    
    const key = `${tier}-${annual ? 'annual' : 'monthly'}`;
    return priceMapping[key as keyof typeof priceMapping];
  }, []);
  
  /**
   * Creates or updates a subscription
   * @param tier The subscription tier to subscribe to
   * @param annual Whether to bill annually or monthly
   * @returns Promise that resolves to client secret for payment
   */
  const createSubscription = useCallback(async (
    tier: 'basic' | 'premium', 
    annual: boolean
  ): Promise<{ clientSecret: string | null; status: string }> => {
    if (!profile?.id) {
      throw new Error('User profile not found');
    }
    
    const patientName = profile.name?.[0]?.text || 
      [profile.name?.[0]?.given?.join(' '), profile.name?.[0]?.family].filter(Boolean).join(' ');
    
    const patientEmail = profile.telecom?.find(t => t.system === 'email')?.value;
    
    // Determine action based on subscription status
    const action = subscriptionStatus === 'active' ? 'upgrade' : 'create';
    
    // Get price ID for the selected tier and billing cycle
    const priceId = getPriceId(tier, annual);
    
    console.log('Starting subscription process:', { 
      tier, 
      annual, 
      priceId, 
      action, 
      patientId: profile.id,
      hasEmail: !!patientEmail
    });
    
    // Execute the subscription bot
    try {
      const botParams = {
        resourceType: 'Parameters',
        parameter: [
          {
            name: 'resourceType',
            valueString: 'Patient'
          },
          {
            name: 'patientId',
            valueString: profile.id
          },
          {
            name: 'priceId',
            valueString: priceId
          },
          {
            name: 'action',
            valueString: action
          },
          {
            name: 'productType',
            valueString: 'FEELHEARD'
          },
          {
            name: 'customerName',
            valueString: patientName
          },
          ...(patientEmail ? [{
            name: 'customerEmail',
            valueString: patientEmail
          }] : [])
        ]
      };
      
      console.log('Calling create-subscription bot with params:', JSON.stringify(botParams, null, 2));
      
      const response = await medplum.executeBot(
        {
          system: 'https://progressnotes.app',
          value: 'create-subscription'
        },
        botParams
      );
      
      console.log('Bot response received:', JSON.stringify(response, null, 2));
      
      // Parse response parameters
      const clientSecret = response.parameter?.find(
        (p: { name: string; valueString?: string }) => p.name === 'clientSecret'
      )?.valueString || null;
      
      const status = response.parameter?.find(
        (p: { name: string; valueString?: string }) => p.name === 'status'
      )?.valueString || 'unknown';
      
      // Check if the response is an OperationOutcome (error)
      if (response.resourceType === 'OperationOutcome' && response.issue && response.issue.length > 0) {
        console.error('Bot returned an error:', response.issue.map((i: any) => i.details?.text || i.diagnostics || 'Unknown error').join(', '));
      }
      
      console.log('Processed bot response:', { 
        hasClientSecret: !!clientSecret, 
        clientSecret: clientSecret ? `${clientSecret.substring(0, 10)}...` : null,
        status,
        isError: response.resourceType === 'OperationOutcome'
      });
      
      return { clientSecret, status };
    } catch (error) {
      console.error('Error calling create-subscription bot:', error);
      throw error;
    }
  }, [medplum, profile, subscriptionStatus, getPriceId]);

  return {
    // Subscription info
    subscriptionStatus,
    subscriptionTier,
    billingCycle,
    isTrialActive,
    trialExpired,
    isFree,
    isActive,
    isPremium,
    isBasic,
    trialEndDate,
    trialDaysRemaining,

    // Usage and limits - kept for backward compatibility, but no longer actively used
    conversationCount,
    conversationLimit,

    // Feature flags
    canUseVoice,
    hasUnlimitedHistory,
    canStartNewConversation,

    // Actions
    createSubscription,
    getPriceId,

    // Raw data for advanced usage
    profile
  };
}